import { keyframes, styled } from "goober";

const cursorTour = keyframes`
  0%, 13% { translate: 34px 36px; }
  20%, 33% { translate: 108px 39px; }
  40%, 53% { translate: 106px 82px; }
  60%, 73% { translate: 180px 37px; }
  80%, 92% { translate: 251px 38px; }
  100% { translate: 34px 36px; }
`;

const cursorPress = keyframes`
  0%, 1%, 4%, 21%, 24%, 41%, 44%, 61%, 64%, 81%, 84%, 100% { transform: scale(1); }
  2.5%, 22.5%, 42.5%, 62.5%, 82.5% { transform: scale(0.88); }
`;

/* Only opacity is ever keyframed: colors resolved inside @keyframes go stale
   when the color scheme flips mid-animation, so the active look lives on a
   statically-colored overlay that fades in. */
const stepActive = keyframes`
  0%, 18% { opacity: 0; }
  22%, 32% { opacity: 1; }
  37%, 100% { opacity: 0; }
`;

const connectorActive = keyframes`
  0%, 19% { opacity: 0; }
  22%, 33% { opacity: 1; }
  38%, 100% { opacity: 0; }
`;

const dashFlow = keyframes`
  to { stroke-dashoffset: -3; }
`;

/* Everything shares one 10s clock; each element's phase is shifted with a
   negative delay of (its cursor-dwell start − the 20% the keyframes are
   authored around). */
const STEPS = [
  { left: 12, top: 22, color: "orange", delay: "-2s", nameWidth: "62%" },
  {
    left: 84,
    top: 22,
    color: "sky",
    delay: "0s",
    nameWidth: "50%",
    parked: true,
  },
  { left: 84, top: 66, color: "purple", delay: "-8s", nameWidth: "58%" },
  { left: 156, top: 22, color: "teal", delay: "-6s", nameWidth: "44%" },
  { left: 228, top: 22, color: "pink", delay: "-4s", nameWidth: "54%" },
];

const CONNECTORS = [
  { d: "M62,34 L82,34", delay: "0s", parked: true },
  { d: "M134,34 L154,34", delay: "-6s" },
  { d: "M206,34 L226,34", delay: "-4s" },
  {
    d: "M62,34 L67,34 Q72,34 72,39 L72,73 Q72,78 77,78 L82,78",
    delay: "-8s",
  },
  {
    d: "M134,78 L139,78 Q144,78 144,73 L144,39 Q144,34 149,34 L154,34",
    delay: "-6s",
  },
];

const Scene = styled("div")`
  position: absolute;
  inset: 0;
`;

const Lines = styled("svg")`
  position: absolute;
  inset: 0;
  overflow: visible;
  pointer-events: none;
`;

const BasePath = styled("path")`
  stroke: var(--gray-400);
  stroke-width: 1;
  fill: none;
  stroke-linecap: round;
`;

const ActivePath = styled("path")`
  stroke: var(--gray-1000);
  stroke-width: 1;
  fill: none;
  stroke-linecap: round;
  stroke-dasharray: 1 2;
  opacity: 0;
  animation:
    ${connectorActive} 10s linear infinite,
    ${dashFlow} 700ms linear infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    &[data-parked] {
      opacity: 1;
    }
  }
`;

const MiniCard = styled("div")`
  position: absolute;
  width: 48px;
  height: 24px;
  border-radius: 6px;
  padding: 5px;
  display: flex;
  gap: 4px;
  background: var(--c200);
  box-shadow: 0 0 0 2px var(--surface-secondary);
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 6px;
    background: var(--c400);
    box-shadow: inset 0 0 0 1px var(--c600);
    opacity: 0;
    animation: ${stepActive} 10s ease infinite;
    animation-delay: var(--phase);
  }
  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
    }
    &[data-parked]::before {
      opacity: 1;
    }
  }
`;

const MiniBlob = styled("div")`
  position: relative;
  width: 2px;
  height: 100%;
  border-radius: 1px;
  background: var(--c600);
  flex-shrink: 0;
`;

const MiniBody = styled("div")`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
`;

const MiniNameBar = styled("div")`
  height: 5px;
  border-radius: 2.5px;
  background: var(--c600);
  opacity: 0.45;
`;

const MiniCounterPill = styled("div")`
  width: 10px;
  height: 5px;
  border-radius: 2.5px;
  background: var(--c600);
  opacity: 0.3;
`;

const Cursor = styled("div")`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  translate: 108px 39px;
  animation: ${cursorTour} 10s cubic-bezier(0.55, 0.06, 0.15, 1) infinite;
  filter: drop-shadow(0 1px 1.5px var(--border-alpha));
  svg {
    display: block;
    transform-origin: 2.5px 2.5px;
    animation: ${cursorPress} 10s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    svg {
      animation: none;
    }
  }
`;

export function HeroScene() {
  return (
    <Scene aria-hidden>
      <Lines width="288" height="112" viewBox="0 0 288 112">
        {CONNECTORS.map(({ d }) => (
          <BasePath key={d} d={d} />
        ))}
        {CONNECTORS.map(({ d, delay, parked }) => (
          <ActivePath
            key={d}
            d={d}
            data-parked={parked || undefined}
            style={{ animationDelay: delay }}
          />
        ))}
      </Lines>
      {STEPS.map(({ left, top, color, delay, nameWidth, parked }) => (
        <MiniCard
          key={color}
          data-parked={parked || undefined}
          style={
            {
              left,
              top,
              "--phase": delay,
              "--c200": `var(--${color}-200)`,
              "--c400": `var(--${color}-400)`,
              "--c600": `var(--${color}-600)`,
            } as React.CSSProperties
          }
        >
          <MiniBlob />
          <MiniBody>
            <MiniNameBar style={{ width: nameWidth }} />
            <MiniCounterPill />
          </MiniBody>
        </MiniCard>
      ))}
      <Cursor>
        {/* Icon mask-renders a hollow silhouette; the cursor needs a filled
            body with a surface-colored casing, so the path is inlined. */}
        <svg width="15" height="15" viewBox="0 0 16 16">
          <path
            d="M2.69133 3.12533C2.66502 3.06461 2.65757 2.99737 2.66995 2.93237C2.68234 2.86736 2.71398 2.80757 2.76078 2.76078C2.80757 2.71398 2.86736 2.68234 2.93237 2.66995C2.99737 2.65757 3.06461 2.66502 3.12533 2.69133L13.792 7.02466C13.8569 7.05108 13.9117 7.09727 13.9489 7.15665C13.986 7.21603 14.0035 7.2856 13.9988 7.35548C13.9942 7.42536 13.9676 7.49201 13.923 7.54595C13.8783 7.59989 13.8178 7.63839 13.75 7.65599L9.66733 8.70933C9.43665 8.76863 9.22607 8.88864 9.05748 9.05688C8.88889 9.22512 8.76844 9.43544 8.70866 9.66599L7.65599 13.75C7.63839 13.8178 7.59989 13.8783 7.54595 13.923C7.49201 13.9676 7.42536 13.9942 7.35548 13.9988C7.2856 14.0035 7.21603 13.986 7.15665 13.9489C7.09727 13.9117 7.05108 13.8569 7.02466 13.792L2.69133 3.12533Z"
            fill="var(--gray-1000)"
            stroke="var(--surface-secondary)"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
      </Cursor>
    </Scene>
  );
}
